import type {PatchDigitalAssetCollectionRequestADto} from '@cohort/admin-schemas/digitalAssetCollection';
import AdminPage from '@cohort/merchants/components/AdminPage';
import FileInput from '@cohort/merchants/components/form/v2/FileInput';
import Input from '@cohort/merchants/components/form/v2/Input';
import LanguageSelectorInput from '@cohort/merchants/components/form/v2/LanguageSelectorInput';
import LocalizedInput from '@cohort/merchants/components/form/v2/LocalizedInput';
import LocalizedTextEditorInput from '@cohort/merchants/components/form/v2/LocalizedTextEditorInput';
import SubmitFooter from '@cohort/merchants/components/form/v2/SubmitFooter';
import {digitalAssetCollectionsKeys} from '@cohort/merchants/hooks/api/DigitalAssetCollections';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useCurrentDigitalAssetCollection} from '@cohort/merchants/hooks/contexts/currentDigitalAssetCollection';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useDigitalAssetCollectionPageStore} from '@cohort/merchants/hooks/stores/digitalAssetCollectionPage';
import {usePrompt} from '@cohort/merchants/hooks/usePrompt';
import {patchDigitalAssetCollection} from '@cohort/merchants/lib/api/DigitalAssetCollections';
import {
  changeSelectedLanguageIfNeeded,
  getDefinedLanguages,
  removeUndefinedLanguages,
} from '@cohort/merchants/lib/form/localization';
import {uploadAsset} from '@cohort/merchants/lib/form/utils';
import {fileValidatorRequired} from '@cohort/merchants/lib/form/validators';
import {trackDigitalAssetCollectionDetailsPageViewed} from '@cohort/merchants/lib/Tracking';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import {
  LanguageSchema,
  LocalizedRichTextSchema,
  LocalizedStringSchema,
} from '@cohort/shared/schema/common';
import type {AssetKind} from '@cohort/shared/schema/common/assets';
import {AllowedAssetMimeTypes} from '@cohort/shared/schema/common/assets';
import {isVideoFile} from '@cohort/shared/utils/mimeTypes';
import {isFile} from '@cohort/shared-frontend/utils/isFile';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment, useEffect} from 'react';
import {useController, useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

const DigitalAssetCollectionFormSchema = z
  .object({
    title: LocalizedStringSchema,
    description: LocalizedRichTextSchema,
    media: z.any().refine(fileValidatorRequired, 'errorRequired'),
    defaultLanguage: LanguageSchema,
    selectedLanguage: LanguageSchema,
    definedLanguages: z.array(LanguageSchema),
    withMaxOwnershipsPerUser: z.boolean(),
    maxOwnershipsPerUser: z.number().nullable(),
    nftEnabled: z.boolean(),
  })
  .superRefine(({description, defaultLanguage}, ctx) => {
    if (description[defaultLanguage] === undefined) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: [`description.${defaultLanguage}`],
      });
    }
  })
  .superRefine(({title, defaultLanguage}, ctx) => {
    if (title[defaultLanguage] === undefined) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: [`title.${defaultLanguage}`],
      });
    }
  })
  .transform(data => {
    data.title = removeUndefinedLanguages(data.title, data.definedLanguages);
    data.description = removeUndefinedLanguages(data.description, data.definedLanguages);
    return data;
  });

type DigitalAssetCollectionFormValues = z.infer<typeof DigitalAssetCollectionFormSchema>;

const DigitalAssetCollectionSettingsPage = (): JSX.Element => {
  const merchant = useCurrentMerchant();
  const queryClient = useQueryClient();
  const digitalAssetCollection = useCurrentDigitalAssetCollection();
  const {t} = useTranslation('pages', {
    keyPrefix: 'digitalAssetCollections.digitalAssetCollection.settings.page',
  });

  const setFooter = useDigitalAssetCollectionPageStore(store => store.setFooter);

  const {register, control, handleSubmit, reset, formState} =
    useForm<DigitalAssetCollectionFormValues>({
      defaultValues: {
        title: digitalAssetCollection.title,
        description: digitalAssetCollection.description,
        media: digitalAssetCollection.imageFileKey,
        defaultLanguage: merchant.defaultLanguage,
        selectedLanguage: merchant.defaultLanguage,
        definedLanguages: getDefinedLanguages(merchant.defaultLanguage, [
          digitalAssetCollection.title,
          digitalAssetCollection.description,
        ]),
        maxOwnershipsPerUser: digitalAssetCollection.maxOwnershipsPerUser,
        withMaxOwnershipsPerUser: digitalAssetCollection.maxOwnershipsPerUser !== null,
        nftEnabled: digitalAssetCollection.nftEnabled,
      },
      resolver: zodResolver(DigitalAssetCollectionFormSchema),
    });

  const selectedLanguage = useWatch({
    control,
    name: 'selectedLanguage',
  });
  const {field: selectedLanguageField} = useController({
    control,
    name: 'selectedLanguage',
  });
  const isDefaultLanguageSelected = selectedLanguage === merchant.defaultLanguage;

  const withMaxOwnershipsPerUser = useWatch({
    control,
    name: 'withMaxOwnershipsPerUser',
  });

  usePrompt(t('promptLostChanges'), formState.isDirty);

  const {isLoading: isUpdating, mutate: updateDigitalAssetCollection} = useCohortMutation({
    mutationFn: async (data: PatchDigitalAssetCollectionRequestADto) =>
      patchDigitalAssetCollection(merchant.id, digitalAssetCollection.id, data),
    notifyErrorMessage: t('notificationErrorUpdate'),
    notifySuccessMessage: t('notificationSuccess'),
    onSuccess: () => {
      queryClient.invalidateQueries(digitalAssetCollectionsKeys.list(merchant.id));
      queryClient.invalidateQueries(
        digitalAssetCollectionsKeys.getById(merchant.id, digitalAssetCollection.id)
      );
      reset({}, {keepValues: true});
    },
  });

  const {isLoading: isUploading, mutateAsync: uploadFile} = useCohortMutation({
    mutationFn: async ({file, assetKind}: {file: File; assetKind: AssetKind}) =>
      uploadAsset(file, assetKind, merchant.id),
    onError: err => notifyError(err, t('errorFileUploadFailed')),
  });

  useEffect(() => {
    trackDigitalAssetCollectionDetailsPageViewed(digitalAssetCollection);
  }, [digitalAssetCollection]);

  useEffect(() => {
    if (formState.isDirty) {
      setFooter(
        <SubmitFooter
          formName="digital-asset-collection-form"
          submitLabel={t('buttonSaveAndPublish')}
          loading={isUpdating || isUploading}
        />
      );
    } else {
      setFooter(undefined);
    }
    return () => setFooter(undefined);
  }, [formState.isDirty, setFooter, t, isUpdating, isUploading]);

  const headerConfig = {
    title: t('title'),
    subtitle: t('subtitle'),
  };

  return (
    <form
      id="digital-asset-collection-form"
      onSubmit={handleSubmit(
        async data => {
          const patchData: PatchDigitalAssetCollectionRequestADto = {
            title: data.title,
            description: data.description,
            maxOwnershipsPerUser: withMaxOwnershipsPerUser ? data.maxOwnershipsPerUser : null,
            nftEnabled:
              data.nftEnabled === true && digitalAssetCollection.nftEnabled === false
                ? true
                : undefined,
          };
          // upload image or video if changed
          if (isFile(data.media)) {
            const asset = await uploadFile({
              file: data.media as unknown as File,
              assetKind: 'digitalAssetVisual',
            });
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (asset === null) {
              return notifyError(t('errorFileUploadFailed'));
            }
            if (isVideoFile(asset.mimeType)) {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              patchData.imageFileKey = asset.previewFileKey!;
              patchData.animationFileKey = asset.fileKey;
            } else {
              patchData.imageFileKey = asset.fileKey;
              patchData.animationFileKey = null;
            }
          }
          updateDigitalAssetCollection(patchData);
        },
        errors =>
          changeSelectedLanguageIfNeeded(
            errors,
            merchant.defaultLanguage,
            selectedLanguageField.onChange
          )
      )}
    >
      <AdminPage header={headerConfig}>
        <LanguageSelectorInput
          control={control}
          definedLanguagesPath="definedLanguages"
          selectedLanguagePath="selectedLanguage"
        />
        <LocalizedInput
          type="text"
          name="title"
          label={t('labelTitle')}
          placeholder={t('placeholderTitle')}
          register={register}
          control={control}
          selectedLanguage={selectedLanguage}
          optional={!isDefaultLanguageSelected}
        />
        <LocalizedTextEditorInput
          name="description"
          label={t('labelDescription')}
          placeholder={t('placeholderDescription')}
          register={register}
          control={control}
          selectedLanguage={selectedLanguage}
          optional={!isDefaultLanguageSelected}
        />
        {isDefaultLanguageSelected && (
          <Fragment>
            <FileInput
              label={t('labelVisual')}
              name="media"
              register={register}
              control={control}
              acceptHint={t('instructionVisual')}
              accept={AllowedAssetMimeTypes.digitalAssetVisual.options.join(',')}
              assetKind="digitalAssetVisual"
              description={t('helperVisual')}
              withResize
            />
            <div className="grid h-10 gap-4 [grid-template-columns:max-content_1fr]">
              <Input
                type="checkbox"
                name="withMaxOwnershipsPerUser"
                label={t('withMaxOwnershipsPerUser')}
                labelPosition="right"
                control={control}
                register={register}
              />
              {withMaxOwnershipsPerUser && (
                <Input
                  type="number"
                  name="maxOwnershipsPerUser"
                  label={t('maxOwnershipsPerUser')}
                  className="max-w-[100px]"
                  labelPosition="right"
                  control={control}
                  register={register}
                  min={1}
                />
              )}
            </div>
            <Input
              type="checkbox"
              name="nftEnabled"
              label={t('labelNftEnabled')}
              labelPosition="right"
              control={control}
              register={register}
              disabled={digitalAssetCollection.nftEnabled}
            />
          </Fragment>
        )}
      </AdminPage>
    </form>
  );
};

export default DigitalAssetCollectionSettingsPage;
